/**
 * Print a 4x2 SKU location tag for a warehouse site. Not a carton label.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { ZplLocationSelect } from "@/components/ZplLocationSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import {
  filterSearchableSelectOptions,
  pickSearchableSelectOptionFromSearch,
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/SearchableSelect";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { skuPickerDescriptionLine, skuPickerTitleLine } from "@/lib/mrpSkuPickerDisplay";
import { MRP_LOCATION_PUBLIC_LOCATION_ID } from "@/lib/mrp";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import {
  buildWarehouseHubLocationSelectOptions,
  warehouseHubLocationLabel,
} from "@/lib/warehouseHubLocationSelect";
import {
  fetchWarehouseLocations,
  printWarehouseRecountLocationLabel,
  searchWarehouseSkus,
} from "@/lib/warehouseRecountApi";
import { useInboundZplPrinter } from "@/pages/shipping/inboundZplPrinter";

const LOCATION_LABEL_PAGE_HINT =
  "Prints a 4x2 tag for this SKU at the selected warehouse site. "
  + "Scanning the tag opens location adjust. Quantity is not printed, and on-hand does not change.";

const SKU_SEARCH_DEBOUNCE_MS = 300;
const SKU_SEARCH_MIN_CHARS = 2;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function WarehouseLocationLabelPage(): JSX.Element {
  usePageBreadcrumb("Location label");
  const { showToast } = useHubToast();
  const { station, setStation, selectedPrinter, unavailableMessage, printersQuery } = useInboundZplPrinter();

  const [skuDraft, setSkuDraft] = useState("");
  const [skuDescription, setSkuDescription] = useState<string | null>(null);
  const [skuSearch, setSkuSearch] = useState("");
  const [hubLocationId, setHubLocationId] = useState<number | null>(null);
  const [warehouseSearch, setWarehouseSearch] = useState("");

  const locationsQuery = useQuery({
    queryKey: ["warehouse", "locations"],
    queryFn: fetchWarehouseLocations,
  });

  const debouncedSkuSearch = useDebouncedValue(skuSearch.trim(), SKU_SEARCH_DEBOUNCE_MS);
  const skuSearchQuery = useQuery({
    queryKey: ["warehouse", "sku-search", debouncedSkuSearch],
    queryFn: () => searchWarehouseSkus(debouncedSkuSearch),
    enabled: debouncedSkuSearch.length >= SKU_SEARCH_MIN_CHARS,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (hubLocationId != null) return;
    const locations = locationsQuery.data;
    if (locations == null || locations.length === 0) return;
    const sgs = locations.find(
      (location) => location.hub_location_id === MRP_LOCATION_PUBLIC_LOCATION_ID.sgs,
    );
    setHubLocationId((sgs ?? locations[0]).hub_location_id);
  }, [hubLocationId, locationsQuery.data]);

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return (skuSearchQuery.data ?? []).map((row) => {
      const descriptionLine = skuPickerDescriptionLine(row);
      return {
        value: row.sku,
        label: skuPickerTitleLine(row),
        description: descriptionLine.length > 0 ? descriptionLine : null,
      };
    });
  }, [skuSearchQuery.data]);

  const selectedSkuOption = useMemo((): SearchableSelectOption | null => {
    const trimmed = skuDraft.trim();
    if (trimmed === "") return null;
    const match = skuOptions.find((option) => option.value === trimmed);
    return match ?? { value: trimmed, label: trimmed, description: skuDescription };
  }, [skuDescription, skuDraft, skuOptions]);

  const warehouseOptions = useMemo(
    () => buildWarehouseHubLocationSelectOptions(locationsQuery.data ?? []),
    [locationsQuery.data],
  );

  const selectedLocation = (locationsQuery.data ?? []).find(
    (location) => location.hub_location_id === hubLocationId,
  ) ?? null;
  const selectedWarehouseOption = warehouseOptions.find(
    (option) => option.value === (hubLocationId != null ? String(hubLocationId) : ""),
  ) ?? null;

  const printMutation = useMutation({
    mutationFn: () => {
      const sku = skuDraft.trim();
      const printer = selectedPrinter.trim();
      if (sku === "") {
        throw new Error("Pick a SKU first.");
      }
      if (hubLocationId == null) {
        throw new Error("Pick a warehouse site first.");
      }
      if (selectedLocation?.megaventory_location_id == null) {
        throw new Error("That warehouse site has no Megaventory location.");
      }
      if (printer === "") {
        throw new Error("Pick a label printer first.");
      }
      const selectedPrinterRow = printersQuery.data?.printers.find((row) => row.name === printer);
      const stockSize = selectedPrinterRow?.labelSize?.trim();
      if (stockSize != null && stockSize !== "" && stockSize !== "4x2") {
        throw new Error(`Selected printer is ${stockSize} stock. Location labels are 4x2.`);
      }
      return printWarehouseRecountLocationLabel({
        sku,
        hubLocationId,
        printer,
      });
    },
    onSuccess: (result) => {
      const site = selectedLocation != null ? warehouseHubLocationLabel(selectedLocation) : "that site";
      showToast({
        message: `Location label for ${result.sku} at ${site} sent to the printer.`,
        tone: "success",
      });
    },
    onError: (error) => {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    },
  });

  const canPrint =
    skuDraft.trim() !== ""
    && hubLocationId != null
    && selectedLocation?.megaventory_location_id != null
    && selectedPrinter.trim() !== ""
    && !printMutation.isPending;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={LOCATION_LABEL_PAGE_HINT}>Location label</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Print SKU location tag</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {locationsQuery.error != null ? (
            <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4" />
              {locationsQuery.error instanceof ApiError
                ? locationsQuery.error.message
                : "Could not load warehouse sites."}
            </p>
          ) : null}

          <div>
            <Label htmlFor="warehouse-location-label-sku">SKU</Label>
            <SearchableSelect
              id="warehouse-location-label-sku"
              presentation="panelSearch"
              className="text-base"
              value={skuDraft}
              options={skuOptions}
              searchValue={skuSearch}
              onSearchChange={setSkuSearch}
              placeholder="SKU or description"
              searchPlaceholder="SKU or description"
              emptyMessage={
                skuSearchQuery.isFetching && debouncedSkuSearch === skuSearch.trim()
                  ? "Searching…"
                  : "No matching items"
              }
              isLoading={skuSearchQuery.isFetching && debouncedSkuSearch === skuSearch.trim()}
              selectedLabelOverride={selectedSkuOption?.label ?? null}
              disabled={printMutation.isPending}
              onValueChange={(option) => {
                if (option == null) {
                  setSkuDraft("");
                  setSkuDescription(null);
                  setSkuSearch("");
                  return;
                }
                const row = (skuSearchQuery.data ?? []).find((entry) => entry.sku === option.value);
                setSkuDraft(option.value);
                setSkuDescription(row != null ? skuPickerDescriptionLine(row) : (option.description ?? null));
                setSkuSearch("");
              }}
              onSearchInputKeyDown={(event) => {
                if (event.key !== "Enter") return;
                const filtered = filterSearchableSelectOptions(skuOptions, skuSearch);
                const picked = pickSearchableSelectOptionFromSearch(filtered, skuSearch);
                if (picked == null) return;
                event.preventDefault();
                const row = (skuSearchQuery.data ?? []).find((entry) => entry.sku === picked.value);
                setSkuDraft(picked.value);
                setSkuDescription(row != null ? skuPickerDescriptionLine(row) : (picked.description ?? null));
                setSkuSearch("");
              }}
            />
            {skuDraft.trim() !== "" && (selectedSkuOption?.description ?? "") !== "" ? (
              <p className="mt-1 text-sm text-slate-600">{selectedSkuOption?.description}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="warehouse-location-label-site">Warehouse site</Label>
            <SearchableSelect
              id="warehouse-location-label-site"
              presentation="panelSearch"
              className="text-base"
              value={hubLocationId != null ? String(hubLocationId) : ""}
              options={warehouseOptions}
              searchValue={warehouseSearch}
              onSearchChange={setWarehouseSearch}
              placeholder="Select warehouse"
              searchPlaceholder="Search warehouses"
              selectedLabelOverride={selectedWarehouseOption?.label ?? null}
              disabled={printMutation.isPending || locationsQuery.isLoading}
              onValueChange={(option) => {
                if (option == null || option.disabled) return;
                const parsed = Number.parseInt(option.value, 10);
                if (!Number.isFinite(parsed) || parsed <= 0) return;
                setHubLocationId(parsed);
                setWarehouseSearch("");
              }}
            />
          </div>

          <div>
            <ZplLocationSelect
              id="warehouse-location-label-printer"
              size="touch"
              value={station}
              onChange={setStation}
              unavailableMessage={unavailableMessage}
              disabled={printMutation.isPending}
            />
          </div>

          <Button
            type="button"
            className="min-h-12 text-base"
            disabled={!canPrint}
            onClick={() => printMutation.mutate()}
          >
            {printMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Print location label
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
